// React
import React, {useMemo} from 'react'

// Third party
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'

// Chackra
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Select,
    Text,
    FormControl,
    FormLabel,
    VisuallyHidden
} from '@chakra-ui/react'

// OOTB Components
import {Button} from '@salesforce/retail-react-app/app/components/shared/ui/Button'

// Custom Icons
import {ChevronDownIcon, LoaderIcon} from '../custom-icons'

import useSiteType from '../../hooks/use-site-type'

/**
 * CountrySelectorModalContent is a React functional component that renders a modal
 * allowing users to change their country selection. It provides a user interface
 * for selecting a country from a dropdown list and confirming the selection.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.isOpen - Determines if the modal is open or closed.
 * @param {function} props.onClose - Callback function to close the modal.
 * @param {string} props.geolocCountry - The name of the country detected by geolocation.
 * @param {string} props.geolocCountryCode - The country code detected by geolocation.
 * @param {Array<Object>} props.countries - An array of country objects available for selection.
 * @param {function} props.handleCountryChange - Callback function to handle changes in country selection.
 * @param {function} props.handleConfirm - Callback function to handle the confirmation of the selected country.
 * @param {boolean} props.isLoading - Indicates if the confirmation button should display a loading state.
 *
 * @returns {JSX.Element} The rendered modal component for country selection.
 */
const CountrySelectorModalContent = ({
    isOpen,
    onClose,
    geolocCountry,
    geolocCountryCode,
    shipToCountryCode,
    countries,
    handleCountryChange,
    handleConfirm,
    isLoading,
    isCheckout,
    showWarning,
    warningType,
    selectedCountryCode
}) => {
    /**
     * Generates a memoized list of country options for a select input element.
     *
     * This function utilizes the `useMemo` hook to optimize performance by memoizing
     * the list of country options. It iterates over the `countries` array and creates
     * an array of `<option>` elements, each representing a country with its respective
     * code and name. The memoized value will only be recalculated when the `countries`
     * array changes.
     *
     * @returns {Array<JSX.Element>} An array of `<option>` elements, each containing
     * the country code as the `value` attribute and the country name as the display text.
     *
     * @example
     * // Example usage within a select element
     * <select>
     *   {countryOptions}
     * </select>
     *
     * @dependencies
     * - `countries`: An array of country objects, where each object contains:
     *   - `code` (string): The unique code representing the country.
     *   - `name` (string): The name of the country.
     */
    const countryOptions = useMemo(() => {
        return countries.map((country) => (
            <option key={country.code} value={country.code}>
                {country.name}
            </option>
        ))
    }, [countries])

    const geolocCountryLocalizedName = useMemo(() => {
        return countries.find((c) => c.code === geolocCountryCode)?.name || geolocCountry
    }, [countries, geolocCountryCode])

    const selectedCountryName = useMemo(() => {
        return countries.find((c) => c.code === selectedCountryCode)?.name || selectedCountryCode
    }, [countries, selectedCountryCode])

    const {isUS} = useSiteType()
    if (shipToCountryCode === '') {
        shipToCountryCode = isUS ? 'US' : 'FR'
    }

    const shipToCountryName = useMemo(() => {
        return countries.find((c) => c.code === shipToCountryCode)?.name || shipToCountryCode
    }, [countries, shipToCountryCode])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="small"
            isCentered
            closeOnOverlayClick={false}
            closeOnEsc={false}
            trapFocus={true}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <FormattedMessage
                        id="country_selector_modal.title"
                        defaultMessage="SELECT YOUR SHIPPING COUNTRY"
                    />
                </ModalHeader>
                <ModalBody>
                    <Text variant="bodyLarge2" mb="1rem">
                        <FormattedMessage
                            id="country_selector_modal.description"
                            defaultMessage="You are currently visiting Vilebrequin.com from {country}. Would you like to update your shipping location?"
                            values={{country: geolocCountryLocalizedName}}
                        />
                    </Text>
                    <FormControl variant="floating">
                        <Select
                            onChange={handleCountryChange}
                            id="selectCountry"
                            variant="basic"
                            defaultValue={geolocCountryCode}
                            icon={<ChevronDownIcon />}
                        >
                            {countryOptions}
                        </Select>
                        <VisuallyHidden>
                            <FormLabel htmlFor="selectCountry">
                                <FormattedMessage
                                    id="drawer_menu.button.ship_to_country"
                                    defaultMessage="Delivery country: "
                                />
                            </FormLabel>
                        </VisuallyHidden>
                    </FormControl>
                    {showWarning && warningType == 'siteSwitch' && (
                        <Text
                            variant="bodyLarge2"
                            mb="1rem"
                            color="red"
                            fontWeight="bold"
                            mt={16}
                            mb={0}
                        >
                            <FormattedMessage
                                id="country_selector_modal.warning.site_switch"
                                defaultMessage="By changing your shipping destination, you are entering a new store. Your current cart cannot be transferred and will be reset."
                            />
                        </Text>
                    )}
                    {showWarning && warningType == 'sameSiteCountrySwitch' && (
                        <Text
                            variant="bodyLarge2"
                            mb="1rem"
                            color="red"
                            fontWeight="bold"
                            mt={16}
                            mb={0}
                        >
                            <FormattedMessage
                                id="country_selector_modal.warning.same_site_country_switch"
                                defaultMessage="You will be redirected to the cart page as products might not be available for {country}"
                                values={{country: selectedCountryName}}
                            />
                        </Text>
                    )}
                </ModalBody>
                <ModalFooter width="full" p="0" flexDirection="column" gap={16}>
                    <Button
                        variant="primary"
                        onClick={() => handleConfirm()}
                        isLoading={isLoading}
                        spinner={<LoaderIcon size={8} color="white" />}
                        w="full"
                    >
                        <FormattedMessage
                            id="country_selector_modal.button.confirm"
                            defaultMessage="Confirm"
                        />
                    </Button>
                    {!isCheckout && (
                        <Button
                            variant="link"
                            onClick={() => handleConfirm(shipToCountryCode)}
                            isLoading={isLoading}
                            spinner={<LoaderIcon size={8} color="black" />}
                            w="full"
                        >
                            <FormattedMessage
                                id="country_selector_modal.button.stay"
                                defaultMessage="No, I want to stay on vilebrequin.com from {country}."
                                values={{country: shipToCountryName}}
                            />
                        </Button>
                    )}
                    {isCheckout && (
                        <Button
                            variant="link"
                            onClick={onClose}
                            isLoading={isLoading}
                            spinner={<LoaderIcon size={8} color="black" />}
                            w="full"
                        >
                            <FormattedMessage
                                id="country_selector_modal.button.keep"
                                defaultMessage="Keep current destination"
                            />
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

CountrySelectorModalContent.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    geolocCountry: PropTypes.string,
    geolocCountryCode: PropTypes.string,
    shipToCountryCode: PropTypes.string,
    countries: PropTypes.array.isRequired,
    handleCountryChange: PropTypes.func.isRequired,
    handleConfirm: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isCheckout: PropTypes.bool.isRequired,
    showWarning: PropTypes.bool,
    warningType: PropTypes.string,
    selectedCountryCode: PropTypes.string
}

export default CountrySelectorModalContent
